import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const UISettingsContext = createContext(null);

export const useUISettings = () => {
  const ctx = useContext(UISettingsContext);
  if (!ctx) {
    throw new Error('useUISettings must be used within UISettingsProvider');
  }
  return ctx;
};

const defaultSettings = {
  textScale: 'medium', // 'tiny' | 'small' | 'medium' | 'large'
  tabLabelMode: 'icon-text', // 'icon-only' | 'icon-text'
  popupPosition: { x: 20, y: 80 },
};

const scaleToRootFontPx = {
  tiny: 14,
  small: 15,
  medium: 16,
  large: 18,
};

export const UISettingsProvider = ({ children }) => {
  const [textScale, setTextScale] = useState(() => {
    return localStorage.getItem('ui:textScale') || defaultSettings.textScale;
  });
  const [tabLabelMode, setTabLabelMode] = useState(() => {
    return localStorage.getItem('ui:tabLabelMode') || defaultSettings.tabLabelMode;
  });
  const [popupPosition, setPopupPosition] = useState(() => {
    try {
      const raw = localStorage.getItem('ui:popupPosition');
      return raw ? JSON.parse(raw) : defaultSettings.popupPosition;
    } catch {
      return defaultSettings.popupPosition;
    }
  });

  useEffect(() => {
    localStorage.setItem('ui:textScale', textScale);
    const px = scaleToRootFontPx[textScale] || scaleToRootFontPx.medium;
    document.documentElement.style.fontSize = `${px}px`;
  }, [textScale]);

  useEffect(() => {
    localStorage.setItem('ui:tabLabelMode', tabLabelMode);
  }, [tabLabelMode]);

  useEffect(() => {
    try {
      localStorage.setItem('ui:popupPosition', JSON.stringify(popupPosition));
    } catch {}
  }, [popupPosition]);

  const value = useMemo(
    () => ({ textScale, setTextScale, tabLabelMode, setTabLabelMode, popupPosition, setPopupPosition }),
    [textScale, tabLabelMode, popupPosition]
  );

  return <UISettingsContext.Provider value={value}>{children}</UISettingsContext.Provider>;
};

export default UISettingsContext;


